import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { AssignReservationRequest, GroupInvitee, GroupRoomBlock } from '../../types/groups';
import { plural } from '../../lib/plural';

const STATUS_OPTIONS = ['booked', 'held', 'invoiced', 'paid', 'new', 'contacted'];

/**
 * Host-initiated reservation assignment: assign a room block to a specific guest
 * (typically a room the host booked manually). Assigning only SAVES the
 * reservation — the host sends the stay confirmation explicitly from the Requests
 * list. Distinct from MarkBookedDialog, which updates a guest-submitted request.
 */
export default function AssignReservationDialog({
  invitees,
  roomBlocks,
  saving,
  onClose,
  onAssign,
}: {
  invitees: GroupInvitee[];
  roomBlocks: GroupRoomBlock[];
  saving: boolean;
  onClose: () => void;
  onAssign: (body: AssignReservationRequest) => void;
}) {
  const [inviteeId, setInviteeId] = useState('');
  const [roomBlockId, setRoomBlockId] = useState(roomBlocks[0]?.id || '');
  const [status, setStatus] = useState('booked');
  const [confirmation, setConfirmation] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [partySize, setPartySize] = useState('1');

  const block = useMemo(
    () => roomBlocks.find((b) => b.id === roomBlockId),
    [roomBlocks, roomBlockId],
  );
  const invitee = useMemo(
    () => invitees.find((i) => i.id === inviteeId),
    [invitees, inviteeId],
  );

  // Only what the host typed. This used to fall back to the block's nightly
  // rate, which stored a headline figure nobody had checked as the amount this
  // guest owes — and rates vary within a block. Pricing belongs to Mark booked,
  // where it is entered in full and confirmed before a guest ever sees it.
  const [rateAmount, setRateAmount] = useState('');
  const [rateCurrency, setRateCurrency] = useState('');
  const effectiveCurrency = rateCurrency || block?.currency || 'USD';

  const qtyNum = Number(quantity);
  const overbooked = block != null && Number.isFinite(qtyNum) && qtyNum > block.availableRooms;

  const canSubmit = roomBlockId !== '' && inviteeId !== '' && STATUS_OPTIONS.includes(status);

  const handleAssign = () => {
    if (!canSubmit) return;
    onAssign({
      roomBlockId,
      inviteeId,
      status,
      bookedConfirmation: confirmation.trim() || undefined,
      arrivalDate: arrivalDate || undefined,
      departureDate: departureDate || undefined,
      quantity: Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : 1,
      partySize: Number.isFinite(Number(partySize)) && Number(partySize) > 0 ? Number(partySize) : 1,
      rateAmount:
        rateAmount !== '' && Number.isFinite(Number(rateAmount)) ? Number(rateAmount) : undefined,
      rateCurrency: effectiveCurrency.trim() ? effectiveCurrency.trim().toUpperCase() : undefined,
    });
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Assign a reservation to a guest</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {roomBlocks.length === 0 && (
            <Alert severity="warning">Add a room block first — a reservation needs a hotel to point at.</Alert>
          )}
          <TextField
            select
            fullWidth
            label="Guest"
            value={inviteeId}
            onChange={(e) => setInviteeId(e.target.value)}
            helperText={invitee?.email ? `Confirmation goes to ${invitee.email}` : 'Pick a guest from the invitee list'}
          >
            {invitees.map((i) => (
              <MenuItem key={i.id} value={i.id}>
                {i.fullName}
                {i.email ? ` · ${i.email}` : ''}
                {i.segment ? ` · ${i.segment}` : ''}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Hotel / room block"
            value={roomBlockId}
            onChange={(e) => setRoomBlockId(e.target.value)}
          >
            {roomBlocks.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.hotelName} · {b.roomType} ({b.availableRooms} left)
              </MenuItem>
            ))}
          </TextField>

          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              sx={{ width: 160 }}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Confirmation number"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              label="Check-in"
              type="date"
              value={arrivalDate}
              onChange={(e) => setArrivalDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Check-out"
              type="date"
              value={departureDate}
              onChange={(e) => setDepartureDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Rooms"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              sx={{ width: 110 }}
            />
            <TextField
              label="Party size"
              type="number"
              value={partySize}
              onChange={(e) => setPartySize(e.target.value)}
              sx={{ width: 130 }}
            />
            <TextField
              fullWidth
              label="Rate (per night)"
              type="number"
              value={rateAmount}
              onChange={(e) => setRateAmount(e.target.value)}
              placeholder={block?.nightlyRate != null ? String(block.nightlyRate) : undefined}
              helperText={
                block?.nightlyRate != null
                  ? `Block rate is ${block.nightlyRate}. Optional — the full price goes in Mark booked.`
                  : 'Optional — the full price goes in Mark booked.'
              }
            />
            <TextField
              label="Currency"
              value={effectiveCurrency}
              onChange={(e) => setRateCurrency(e.target.value)}
              inputProps={{ maxLength: 3 }}
              sx={{ width: 110 }}
            />
          </Stack>

          {overbooked && (
            <Alert severity="warning">
              {block?.hotelName} has {plural(block?.availableRooms ?? 0, 'room')} left but you're assigning {qtyNum}. You can still
              proceed — this is your manual reservation.
            </Alert>
          )}
          {status === 'booked' && (
            <Alert severity="info">
              Saved to the reservation — send the guest their confirmation from the Requests list when you're ready.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit || saving} onClick={handleAssign}>
          {saving ? 'Assigning…' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
